import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import ProductCard from '../components/ProductCard';
import Chip from '../components/Chip';
import Footer from '../components/Footer';

const fetchProducts = async () => {
  const { data } = await axios.get(`${apiUrl}/menuItem`);
  return data.list || [];
};

const fetchCategories = async () => {
  const { data } = await axios.get(`${apiUrl}/categories`);
  return data.list || [];
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState('default');
  const [cats, setCats] = useState([]);

  const { data: products, isLoading } = useQuery({ queryKey: ['products'], queryFn: fetchProducts });
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: fetchCategories });

  useEffect(() => {
    setQ(searchParams.get('q') || '');
  }, [searchParams]);

  const handleSearch = (value) => {
    setQ(value);
    setSearchParams(value ? { q: value } : {});
  };

  const toggleCat = (id) =>
    setCats((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  let results = products ?? [];
  if (q) {
    const lower = q.toLowerCase();
    results = results.filter(
      (p) =>
        p.name?.toLowerCase().includes(lower) ||
        p.description?.toLowerCase().includes(lower) ||
        p.desc?.toLowerCase().includes(lower) ||
        p.category?.name?.toLowerCase().includes(lower)
    );
  }
  if (cats.length) results = results.filter((p) => cats.includes(p.category?.id));
  if (sort === 'price-asc') results = [...results].sort((a, b) => a.price - b.price);
  if (sort === 'price-desc') results = [...results].sort((a, b) => b.price - a.price);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <nav className="text-sm text-gray-400 mb-3 flex items-center gap-2">
            <Link to="/" className="hover:text-orange-500 transition">Home</Link>
            <span>/</span>
            <span className="text-gray-700 font-semibold">Search</span>
          </nav>
          <h1 className="font-serif text-4xl text-gray-900">
            {q ? (
              <>Results for <span className="text-[#f97316]">"{q}"</span></>
            ) : (
              'Search Menu'
            )}
          </h1>
          {!isLoading && q && (
            <p className="text-gray-400 mt-1">
              {results.length} dish{results.length !== 1 ? 'es' : ''} found
            </p>
          )}
        </div>

        {/* Search input */}
        <div className="relative mb-6">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">🔍</span>
          <input
            value={q}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search dishes, ingredients, categories..."
            className="w-full pl-12 pr-12 py-4 border border-gray-200 rounded-2xl text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            autoFocus
          />
          {q && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
            >
              ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8 items-center">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-2xl text-sm bg-white cursor-pointer"
          >
            <option value="default">Sort: Default</option>
            <option value="price-asc">Price: Low → High</option>
            <option value="price-desc">Price: High → Low</option>
          </select>

          {(categories || []).map((cat) => (
            <Chip key={cat.id} active={cats.includes(cat.id)} onClick={() => toggleCat(cat.id)}>
              {cat.name}
            </Chip>
          ))}

          {cats.length > 0 && (
            <button
              onClick={() => setCats([])}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-100 animate-pulse h-64" />
            ))}
          </div>
        ) : results.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {results.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <p className="text-6xl mb-4">🍽</p>
            <p className="text-xl font-bold text-gray-800 mb-2">No dishes found</p>
            <p className="text-gray-400 text-sm mb-6">
              {q
                ? `No results for "${q}". Try different keywords.`
                : 'Start typing to search for dishes.'}
            </p>
            <Link
              to="/menu"
              className="bg-orange-500 text-white px-6 py-3 rounded-2xl font-bold hover:bg-orange-600 transition inline-block"
            >
              Browse Full Menu
            </Link>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
